import { shadowQueued, type Request, type RequestStatus } from '@dia/db';

/**
 * Working a request, as pure functions (migration 022).
 *
 * The guest card reads requests.status and shows it as Received, On it or
 * Done. This is the other end: the ops request screen moves that same column,
 * with the values the table has always had. Nothing is invented here.
 *
 *   seen          "Ya lo vi"            the guest still reads Received,
 *   in_progress   "Lo estoy haciendo"   the guest reads On it,
 *   done          "Ya está listo"       the guest reads Done.
 *
 * new is where a request starts and nobody taps back to it. declined keeps its
 * database value but has no button: saying no to a guest is a conversation
 * first, not a tap.
 */

export const WORK_STEPS = ['seen', 'in_progress', 'done'] as const satisfies readonly RequestStatus[];
export type WorkStep = (typeof WORK_STEPS)[number];

/**
 * The columns one tap writes. Taking a request on, or finishing it, signs it
 * with who did it and when (handled_by, handled_at, which have existed since
 * 006). Marking it seen signs nothing: seeing is not doing.
 */
export function statusPatch(step: WorkStep, staffId: string | null, now: Date): Record<string, unknown> {
  if (step === 'seen') return { status: 'seen' };
  return { status: step, handled_by: staffId, handled_at: now.toISOString() };
}

/**
 * Declining is not a tap among the others: it only happens together with a
 * reply, so the guest is never left with a closed card and no word from
 * anyone. The caller enqueues this alongside the reply message, never alone.
 */
export function declinePatch(staffId: string | null, now: Date): Record<string, unknown> {
  return { status: 'declined', handled_by: staffId, handled_at: now.toISOString() };
}

type QueuedOp = {
  table: string;
  kind: 'insert' | 'update';
  payload: Record<string, unknown>;
  match?: Record<string, unknown>;
};

/**
 * The request as this phone last left it: the island's row with any update
 * still waiting in the outbox laid over it, oldest first. So a tap shows at
 * once, with no signal, and a tap the island has taken is simply the row.
 */
export function withQueuedUpdates(row: Request, ops: readonly QueuedOp[]): Request {
  let out = row;
  for (const op of ops) {
    if (op.table !== 'requests' || op.kind !== 'update') continue;
    const target = op.match?.id ?? null;
    const byClient = op.match?.client_id ?? null;
    if (target !== row.id && byClient !== row.client_id) continue;
    out = { ...out, ...(op.payload as Partial<Request>) };
  }
  return out;
}

/** True while a status tap for this request is still on the phone. */
export function statusQueued(requestId: string, ops: readonly QueuedOp[]): boolean {
  return ops.some(
    (op) => op.table === 'requests' && op.kind === 'update' && op.match?.id === requestId && 'status' in op.payload,
  );
}

/**
 * Requests that reached the island in English with no Spanish: a guest who
 * wrote with no signal, mostly. The guest's own phone attaches the Spanish when
 * it next opens the thread with signal; the queue does it too, the first time
 * somebody on it looks with signal, so the crew are never left waiting on a
 * guest who put the phone away. Whichever lands first stays (migration 022).
 */
export function owedSpanish(requests: readonly Request[], ops: readonly QueuedOp[]): Request[] {
  return requests.filter(
    (r) =>
      r.lang_original === 'en' &&
      !r.text_es &&
      !shadowQueued(ops as Parameters<typeof shadowQueued>[0], 'requests', r.client_id),
  );
}
